import {getOwnedNodes, SceneProperties, Parameters, deserializeTemplateGraph} from "@cm/template-nodes"
import {AfterViewInit, Component, inject, Injector, ChangeDetectionStrategy} from "@angular/core"
import {CM_LOGGER_AR} from "@app/common/services/logging"
import {MatSnackBar} from "@angular/material/snack-bar"
import {ActivatedRoute} from "@angular/router"
import {assignGltfDataObject, computeArLodAssignmentKey, getArAssignmentKey, getGltfDataObjects} from "@app/common/helpers/ar/ar"
import {GltfExportService} from "@app/common/services/gltf-export/gltf-export.service"
import {ThreeTemplateSceneProviderComponent} from "@app/template-editor/components/three-template-scene-provider/three-template-scene-provider.component"
import {BakeTextureService} from "@app/template-editor/services/bake-texture.service"
import {MeshGeometryService} from "@app/template-editor/services/mesh-geometry.service"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {ThreeSceneManagerService} from "@app/template-editor/services/three-scene-manager.service"
import {deserializeGraphNode} from "@cm/graph-v3"
import {IsDefined, isInstanceOf} from "@cm/utils"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {AuthService} from "@common/services/auth/auth.service"
import {UploadGqlService} from "@common/services/upload/upload.gql.service"
import {GetTemplateRevisionForBackendArGenerationGQL} from "@pages/ar-generation/backend-ar-generation.generated"
import {removeCachedArData} from "@pages/ar-generation/helpers/helpers"
import {filter, take} from "rxjs"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-backend-ar-generation",
    templateUrl: "./backend-ar-generation.component.html",
    providers: [SceneManagerService, MeshGeometryService, BakeTextureService],
    styleUrls: ["./backend-ar-generation.component.scss"],
    imports: [ThreeTemplateSceneProviderComponent],
})
export class BackendArGenerationComponent implements AfterViewInit {
    private readonly logger = inject(CM_LOGGER_AR)
    readonly auth = inject(AuthService)
    readonly injector = inject(Injector)
    readonly gltfExportService = inject(GltfExportService)

    threeSceneManagerService: ThreeSceneManagerService | undefined
    userAvailable = false

    readonly templateRevisionGql = inject(GetTemplateRevisionForBackendArGenerationGQL)
    private readonly route = inject(ActivatedRoute)
    private readonly uploadService = inject(UploadGqlService)
    private readonly snackBar = inject(MatSnackBar)

    ngAfterViewInit(): void {
        // need to wait for the user to be loaded due to Angular bug in headless mode:
        // component is activated before the `canActivate` guard is resolved
        this.auth.userId$.pipe(filter(IsDefined), take(1)).subscribe(() => {
            this.userAvailable = true
            this.exportSceneIfPossible()
        })
    }

    getTemplateRevisionId(): string {
        const result = this.route.snapshot.queryParamMap.get("templateRevisionId")
        if (!result) throw new Error("The templateRevisionId query parameter is missing.")
        return result
    }

    getParameters() {
        const parameterStringB64 = this.route.snapshot.queryParamMap.get("templateParametersBase64")
        if (!parameterStringB64) throw new Error("The templateParametersBase64 query parameter is missing.")

        const parameters = deserializeGraphNode(JSON.parse(atob(parameterStringB64)), "template")
        if (!(parameters instanceof Parameters)) throw new Error("Wrong parameter types")
        return parameters
    }

    /*Incoming requests either have ar or web lod, because the configurator runs with web lod and we do not want to
    compile a template in ar lod inside the configurator. The lod changes the assignment key (web lod can have more parameters).
    Ar models are always computed in ar lod though. Web lod assignments are then mapped to the corresponding ar lod model.*/
    async setupAndExportScene(threeSceneManagerService: ThreeSceneManagerService) {
        const templateRevisionId = this.getTemplateRevisionId()
        const parameters = this.getParameters()
        const webOrArLodAssignmentKey = getArAssignmentKey(parameters)
        const {templateRevision} = await fetchThrowingErrors(this.templateRevisionGql)({
            id: templateRevisionId,
            assignmentKey: {equals: webOrArLodAssignmentKey},
        })
        if (!templateRevision.template) throw new Error("Cannot generate AR: template is not accessible")

        await removeCachedArData(templateRevision, this.injector)

        this.logger.debug("Exporting scene with assignment key:", webOrArLodAssignmentKey)
        this.logger.debug("Parameters: ", parameters)

        const templateGraph = deserializeTemplateGraph(templateRevision.graph)

        threeSceneManagerService.sceneManagerService.$defaultCustomerId.set(templateRevision.template.organizationLegacyId)

        const arLodAssigmentKey = await computeArLodAssignmentKey(threeSceneManagerService.sceneManagerService, templateRevisionId, parameters, templateGraph)

        const dataObjectAssignments = await getGltfDataObjects(templateRevisionId, arLodAssigmentKey, this.injector)

        if (dataObjectAssignments.length > 0) {
            if (arLodAssigmentKey === webOrArLodAssignmentKey) throw new Error("Ar model was not removed")
            const dataObjectAssignment = dataObjectAssignments[0]
            if (dataObjectAssignment === null) throw new Error("Could not read data object assignment")
            this.logger.debug("Reusing existing data object")
            assignGltfDataObject(dataObjectAssignment.dataObject.id, templateRevisionId, webOrArLodAssignmentKey, this.injector)
        } else {
            this.logger.debug("Creating new ar export")

            const previousSceneProperties = getOwnedNodes(templateGraph).filter(isInstanceOf(SceneProperties))

            //Not reusing subdivision from the existing scene properties will make AR models unusable
            let maxSubdivisionLevel = 9999
            let maxSubdivisionLevelOnMobile: number | undefined = undefined
            previousSceneProperties.forEach((sceneProperty) => {
                maxSubdivisionLevel = Math.min(sceneProperty.parameters.maxSubdivisionLevel, maxSubdivisionLevel)
                if (sceneProperty.parameters.maxSubdivisionLevelOnMobile !== undefined) {
                    if (maxSubdivisionLevelOnMobile === undefined) maxSubdivisionLevelOnMobile = sceneProperty.parameters.maxSubdivisionLevelOnMobile
                    else maxSubdivisionLevelOnMobile = Math.min(sceneProperty.parameters.maxSubdivisionLevelOnMobile, maxSubdivisionLevelOnMobile)
                }
            })

            const glbData = await this.gltfExportService.exportGltfFile(
                templateGraph,
                parameters,
                {
                    maxSubdivisionLevel,
                    maxSubdivisionLevelOnMobile,
                },
                threeSceneManagerService,
                "ar",
                {alphaBlendAsOpaque: true},
            )

            await this.uploadAndAttachGLTFToEntity(
                glbData,
                arLodAssigmentKey,
                webOrArLodAssignmentKey,
                templateRevisionId,
                templateRevision.template.organizationId,
            )

            this.logger.debug("Export and upload completed.")
        }

        //For debugging: FilesService.downloadFile("glb-export.glb", new Blob([glbData]))
    }

    async uploadAndAttachGLTFToEntity(
        glbData: Uint8Array,
        arLodAssignmentKey: string,
        webOrArLodAssignmentKey: string,
        templateRevisionId: string,
        organizationId: string,
    ) {
        const dataObject = await this.uploadService.createAndUploadDataObject(new File([glbData as Uint8Array<ArrayBuffer>], "export.glb"), {
            organizationId,
            mediaType: "model/gltf-binary",
            size: glbData.byteLength,
        })

        await assignGltfDataObject(dataObject.id, templateRevisionId, arLodAssignmentKey, this.injector) //always assign the ar lod model
        if (arLodAssignmentKey !== webOrArLodAssignmentKey)
            await assignGltfDataObject(dataObject.id, templateRevisionId, webOrArLodAssignmentKey, this.injector) //if ar was triggered from web lod with different parameters, assign the ar model also with web lod assignment key
    }

    async exportSceneIfPossible() {
        if (!this.threeSceneManagerService || !this.userAvailable) return
        try {
            await this.setupAndExportScene(this.threeSceneManagerService)
        } catch (e) {
            this.logger.error("Ar generation failed", e)
            this.logger.debug("Template revision: ", this.getTemplateRevisionId)
            this.logger.debug("Parameters: ", this.getParameters().serialize())
            this.logger.debug("Url paramseters: ", this.threeSceneManagerService.sceneManagerService.$configuratorUrlParams())
            throw e
        }
        window.status = "ready" // Let puppeteer know that the AR generation finished.
    }

    onInititalizedThreeSceneManagerService(threeSceneManagerService: ThreeSceneManagerService | undefined) {
        if (threeSceneManagerService) {
            threeSceneManagerService.$ambientLight.set(false)
            threeSceneManagerService.$showGrid.set(false)
            threeSceneManagerService.$displayMode.set("configurator")
        }

        this.threeSceneManagerService = threeSceneManagerService

        this.exportSceneIfPossible()
    }
}
